import { get } from 'lodash-es';
import debounce from 'lodash.debounce';
import React, { useState } from 'react';
import { Query } from 'react-apollo';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import ReactTable from 'react-table';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { AdminTicketingStatistics } from '../../../components/AdminTicketingStatistics';
import { handleRequestError } from '../../../constants/errorCodes';
import { getSessionStorageTicketsFilter, setSessionStorageTicketsFilter } from '../../../constants/LocalStorageKeys';
import {
  TICKET_ACTION_STATUS_ACTION_REQUIRED,
  TICKET_ACTION_STATUS_DONE,
  TICKET_ACTION_STATUS_WAITING,
} from '../../../constants/ticketing';
import { GetTicketsQuery } from '../../../operations/queries/GetTicketsQuery';
import { redirectToAdminSingleTicketPage } from '../../../redux/actions/navigationActions';
import {
  convertIdValueArrayToObject,
  convertKeyValueArrayToObject,
  haveArraysDiverged,
} from '../../../utils/arrayOperations';
import { columns } from './tableColumns';

export const AdminTicketingPage = injectIntl(props => {
  const dispatch = useDispatch();

  const DEFAULT_PAGE_SIZE = 14;
  let reactTableFiltered = null;
  const storedQueryVariables = getSessionStorageTicketsFilter();

  const filters = React.useRef(get(storedQueryVariables, 'filter', {}));

  const [pageSize, setPageSize] = useState(get(storedQueryVariables, 'take', DEFAULT_PAGE_SIZE));
  const [page, setPage] = useState(get(storedQueryVariables, 'skip', 0) / pageSize);

  const defaultQueryVariables = {
    skip: 0,
    take: DEFAULT_PAGE_SIZE,
    filter: {
      actionStatusOr: TICKET_ACTION_STATUS_ACTION_REQUIRED,
    },
    order: { updatedAt: 'DESC' },
  };

  return (
    <Query
      query={GetTicketsQuery}
      variables={storedQueryVariables || defaultQueryVariables}
      onError={error => handleRequestError(error, dispatch)}
      fetchPolicy="no-cache"
      notifyOnNetworkStatusChange={true}>
      {({ loading, data, refetch }) => {
        const tickets = get(data, 'getTickets.tickets', []);
        const ticketCount = get(data, 'getTickets.queryCount', 0);

        const debouncedGetTickets = debounce(refetch, 400);

        const onFetchData = state => {
          if (data) {
            const filtersObject = convertIdValueArrayToObject(state.filtered);
            if ('id' in filtersObject) filtersObject['id'] = parseInt(filtersObject['id'], 10);
            if ('actionStatusOr' in filtersObject && filtersObject['actionStatusOr'] === 'NO')
              filtersObject['actionStatusOr'] = [TICKET_ACTION_STATUS_DONE, TICKET_ACTION_STATUS_WAITING];

            let orderVariables = [];
            state.sorted.forEach(filter => {
              if (filter.id) {
                const fieldName = ['createdAtInterval', 'updatedAtInterval', 'dueDateInterval'].includes(filter.id)
                  ? filter.id.replace('Interval', '')
                  : filter.id;
                orderVariables.push({ [fieldName]: filter.desc ? 'DESC' : 'ASC' });
              }
            });
            const order = convertKeyValueArrayToObject(orderVariables);
            if (order.hasOwnProperty('actionStatusOr')) {
              order.actionStatus = order.actionStatusOr;
              delete order.actionStatusOr;
            }

            const queryVariables = {
              filter: {
                ...filtersObject,
              },
              order,
              skip: state.pageSize !== pageSize ? 0 : state.page * state.pageSize,
              take: state.pageSize,
            };

            setSessionStorageTicketsFilter(queryVariables);
            filters.current = { ...filtersObject };

            if (
              !reactTableFiltered ||
              !haveArraysDiverged(state.filtered, reactTableFiltered, ['id', 'firstName', 'lastName', 'subject'])
            ) {
              reactTableFiltered = state.filtered;
              debouncedGetTickets.cancel();

              refetch(queryVariables);
            } else {
              debouncedGetTickets(queryVariables);
            }
          }
        };
        return (
          <Container fluid>
            <Row>
              <Col xs="12">
                <Row className="mb-3">
                  <Col xs="12">
                    <Card className="hok-card hok-ticketing-card">
                      <CardBody>
                        <AdminTicketingStatistics filters={filters.current} />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <Row>
                  <Col xs="12">
                    <ReactTable
                      className="-striped -highlight"
                      style={{ cursor: 'pointer' }}
                      filterable
                      data={tickets}
                      columns={columns}
                      pageSizeOptions={[DEFAULT_PAGE_SIZE, 25, 50, 100]}
                      defaultPageSize={DEFAULT_PAGE_SIZE}
                      pageSize={pageSize}
                      loading={loading}
                      manual
                      onFetchData={onFetchData}
                      defaultFiltered={
                        get(storedQueryVariables, 'filter', null)
                          ? Object.keys(storedQueryVariables.filter).map(key => {
                              return {
                                id: key,
                                value:
                                  key === 'actionStatusOr'
                                    ? storedQueryVariables.filter[key] === TICKET_ACTION_STATUS_ACTION_REQUIRED
                                      ? TICKET_ACTION_STATUS_ACTION_REQUIRED
                                      : 'NO'
                                    : storedQueryVariables.filter[key],
                              };
                            })
                          : [
                              {
                                id: 'actionStatusOr',
                                value: TICKET_ACTION_STATUS_ACTION_REQUIRED,
                              },
                            ]
                      }
                      defaultSorted={
                        get(storedQueryVariables, 'order', null)
                          ? Object.keys(storedQueryVariables.order).map(key => ({
                              id: key === 'actionStatus' ? 'actionStatusOr' : key,
                              desc: storedQueryVariables.order[key] === 'DESC',
                            }))
                          : [
                              {
                                id: 'updatedAt',
                                desc: true,
                              },
                            ]
                      }
                      pages={Math.ceil(ticketCount / pageSize)}
                      page={page}
                      onPageChange={pageIndex => setPage(pageIndex)}
                      onPageSizeChange={pageSize => {
                        setPageSize(pageSize);
                        setPage(0);
                      }}
                      onFilteredChange={() => {
                        setPage(0);
                      }}
                      getTrProps={(_, rowInfo) => ({
                        onClick: () => {
                          dispatch(redirectToAdminSingleTicketPage(rowInfo.original.number));
                        },
                      })}
                      loadingText={<FormattedMessage id="adminUsersListPage.tableLoading" />}
                      noDataText={<FormattedMessage id="adminUsersListPage.tableNoData" />}
                      previousText={<FormattedMessage id="adminUsersListPage.tablePreviousText" />}
                      nextText={<FormattedMessage id="adminUsersListPage.tableNextText" />}
                      pageText={<FormattedMessage id="adminUsersListPage.tablePageText" />}
                      ofText={<FormattedMessage id="adminUsersListPage.tableOfText" />}
                      rowsText={props.intl.formatMessage({
                        id: 'adminUsersListPage.tableRowsText',
                      })}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        );
      }}
    </Query>
  );
});
